/**
 * ModuleTemplates.tsx — Kreator modułów: pełnoszerokościowy widok obszarów jako sekcji
 * (pionowy stack) z kafelkami modułów per obszar.
 *
 * Lewa kolumna usunięta — obszary stają się nagłówkami sekcji, moduły zawsze jako kafelki.
 * Każda sekcja ma własny przycisk „Dodaj moduł". Przycisk „Dodaj obszar" w nagłówku strony.
 *
 * Szablon = moduł z flagą „szablon kreatora" (modules.is_template_only).
 * Dostęp gated permission `module_builder.designer` (backend 403 → load error).
 *
 * Data:
 *  - GET    /admin/module-areas      → obszary + templateCount
 *  - POST   /admin/module-areas      → create · PATCH /:id → edit (code niezmienny)
 *  - DELETE /admin/module-areas/:id  → 204 / 409 AREA_HAS_MODULES
 *  - GET    /admin/module-templates  → szablony + entityCount + metadane obszaru
 *  - POST   /admin/module-templates  → create → redirect do edytora
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MODULE_TEMPLATE_SLUG_PATTERN } from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { Pencil, Plus, Trash2 } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ColorPicker } from '@/components/configurable-table/ColorPicker';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { KartotekaBadge } from '@/components/KartotekaBadge';
import { Layout } from '@/components/Layout';
import { Modal } from '@/components/Modal';
import { IconPicker, renderIconByName } from '@/components/modules/IconPicker';
import { Skeleton } from '@/components/Skeleton';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import type { KartotekaKind } from '@/lib/kartoteka-kind';

interface ModuleAreaRow {
  id: string;
  code: string;
  name: string;
  icon: string | null;
  color: string | null;
  sortOrder: number | null;
  templateCount: number;
}

interface AreaFormValues {
  code: string;
  name: string;
  icon: string;
  color: string;
}

interface ModuleTemplateRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  clientVersion: string;
  technicalVersion: string;
  /** true = szablon Kreatora (edytowalny); false = moduł systemowy (podgląd
   *  nieedytowalny — Decyzje/KSeF/Symfonia/Magazyny, ADR-0086). */
  isTemplateOnly: boolean;
  areaId: string | null;
  areaCode: string | null;
  areaName: string | null;
  entityCount: number;
  /** Kartoteki (encje) modułu, posortowane — podgląd składu modułu na kaflu.
   *  kind rozróżnia systemową (zielony) od utworzonej w Kreatorze (pomarańczowy). */
  entities: Array<{ name: string; kind: KartotekaKind }>;
  installCount: number;
  /** Autor: imię i nazwisko twórcy / ostatniego edytującego (fallback email); null = nieznany. */
  createdByName: string | null;
  createdAt: string | null;
  updatedByName: string | null;
  updatedAt: string | null;
}

/** Formatuje ISO → czytelna data pl-PL. Pusty string przy braku/błędnej dacie. */
function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pl-PL');
}

function extractErrorCode(err: unknown): string | undefined {
  const ax = err as AxiosError<{ error?: string }>;
  return ax.response?.data?.error;
}

/** Pierwszy komunikat walidacji z odpowiedzi 422 (details: { pole: [msg] }) — do toastu. */
function extractErrorDetail(err: unknown): string | undefined {
  const ax = err as AxiosError<{ details?: Record<string, string[]> }>;
  const details = ax.response?.data?.details;
  if (!details) return undefined;
  for (const messages of Object.values(details)) {
    if (Array.isArray(messages) && messages[0]) return messages[0];
  }
  return undefined;
}

/** Normalizacja kodu/slugu do dozwolonego kształtu [a-z][a-z0-9_]* na żywo przy wpisywaniu. */
function normalizeSlugInput(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

// ---------------------------------------------------------------------------
// Kafelek modułu — reużywalny lokalny komponent
// ---------------------------------------------------------------------------

function ModuleTile({
  tpl,
  onOpen,
  onDelete,
  accentColor,
}: {
  tpl: ModuleTemplateRow;
  onOpen: (slug: string) => void;
  /** Usuń moduł (potwierdzenie obsługuje rodzic). Brak = bez przycisku usuwania. */
  onDelete?: () => void;
  /** Kolor obszaru — w widoku sekcji RZĄDZI nad kolorem modułu (kolor = przynależność do
   *  obszaru). Brak (sekcja „bez obszaru") → fallback na kolor własny modułu. */
  accentColor?: string | null;
}) {
  const { t } = useTranslation();
  // Moduł systemowy (isTemplateOnly=false) = podgląd nieedytowalny: karta nieklikalna
  // (edytor Kreatora filtruje isTemplateOnly=true → otwarcie dałoby 404), bez akcji
  // usuwania, z plakietką „Systemowy". ADR-0086.
  const readOnly = !tpl.isTemplateOnly;
  // crm_core = pakiet fundamentowy. Jest szablonem (isTemplateOnly=true, więc KLIKALNY —
  // edytor struktury bazowej, decyzja 2026-07-01), ale traktujemy go jak systemowy w
  // dwóch rzeczach: plakietka „Systemowy" i BRAK kosza (skasowanie fundamentu jest
  // destrukcyjne). Stąd osobna flaga zamiast doklejania do readOnly (to zabrałoby klik).
  const isFoundation = tpl.slug === 'crm_core';
  const showSystemBadge = readOnly || isFoundation;
  // Kolor obszaru wygrywa, gdy kafel jest w sekcji obszaru — zmiana koloru obszaru
  // przemalowuje wszystkie kafle w nim (także te z własnym kolorem, np. Urlopy).
  const accent = accentColor ?? tpl.color ?? '#e5e7eb';
  const hasColor = Boolean(accentColor ?? tpl.color);
  return (
    <div className="group relative w-full sm:w-96">
      <button
        key={tpl.id}
        type="button"
        onClick={readOnly ? undefined : () => onOpen(tpl.slug)}
        aria-disabled={readOnly || undefined}
        style={{ borderTopColor: accent }}
        className={
          readOnly
            ? 'flex h-full w-full cursor-default flex-col rounded-lg border border-t-4 border-gray-200 bg-white p-4 pr-9 text-left shadow-sm dark:border-gray-700 dark:bg-gray-900'
            : 'flex h-full w-full flex-col rounded-lg border border-t-4 border-gray-200 bg-white p-4 pr-9 text-left shadow-sm transition hover:shadow-md dark:border-gray-700 dark:bg-gray-900'
        }
      >
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
            // Jasny, stonowany kwadrat: tło = kolor z niską przezroczystością, ikona = kolor
            // (currentColor) — mniej „soczyste" niż biała ikona na pełnym kolorze.
            style={{
              backgroundColor: hasColor ? `${accent}1f` : '#f3f4f6',
              color: hasColor ? accent : '#6b7280',
            }}
            aria-hidden="true"
          >
            {renderIconByName(tpl.icon ?? 'Package', 'h-4 w-4')}
          </span>
          <div className="min-w-0">
            <div className="truncate font-medium">{tpl.name}</div>
            <div className="truncate text-xs text-gray-500 dark:text-gray-400">
              <code>{tpl.slug}</code>
            </div>
          </div>
        </div>
        {tpl.description && (
          <p className="mt-2 line-clamp-2 text-sm text-gray-600 dark:text-gray-300">
            {tpl.description}
          </p>
        )}
        {/* Skład modułu — kartoteki (np. Wnioski, Bilanse), cap 4 + „+N".
            Kolor badge = pochodzenie kartoteki (systemowa/utworzona), wspólny KartotekaBadge. */}
        {tpl.entities.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {tpl.entities.slice(0, 4).map((entity) => (
              <KartotekaBadge
                key={entity.name}
                kind={entity.kind}
                name={entity.name}
                className="max-w-[10rem] truncate"
              />
            ))}
            {tpl.entities.length > 4 && (
              <span className="inline-flex items-center rounded-md px-1.5 py-0.5 text-xs text-gray-400">
                {t('moduleTemplates.moreEntities', {
                  count: tpl.entities.length - 4,
                })}
              </span>
            )}
          </div>
        )}
        <div className="mt-3 flex items-center gap-2">
          <Badge color="gray">{t('moduleTemplates.entityCount', { count: tpl.entityCount })}</Badge>
          {tpl.installCount > 0 && (
            <Badge color="green">
              {t('moduleTemplates.installCount', { count: tpl.installCount })}
            </Badge>
          )}
          <Badge color="blue">v{tpl.clientVersion}</Badge>
          {showSystemBadge && <Badge color="gray">{t('moduleTemplates.systemBadge')}</Badge>}
        </div>
        {/* Autor — kto ostatnio dotknął szablonu (fallback: kto utworzył). Seedowane = brak. */}
        {(tpl.updatedByName ?? tpl.createdByName) && (
          <div
            className="mt-2 truncate text-xs text-gray-400 dark:text-gray-500"
            data-testid={`module-author-${tpl.slug}`}
          >
            {tpl.updatedByName
              ? t('moduleTemplates.editedBy', {
                  name: tpl.updatedByName,
                  date: formatDate(tpl.updatedAt),
                })
              : t('moduleTemplates.createdBy', {
                  name: tpl.createdByName,
                  date: formatDate(tpl.createdAt),
                })}
          </div>
        )}
      </button>
      {onDelete && !readOnly && !isFoundation && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={t('moduleTemplates.deleteTemplateAria', { name: tpl.name })}
          title={t('moduleTemplates.deleteTemplateAria', { name: tpl.name })}
          data-testid={`delete-module-${tpl.slug}`}
          className="absolute right-2 top-3 rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-red-600 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-red-400"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function ModuleTemplates() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const notifySuccess = (msg: string) => toast.show({ variant: 'success', description: msg });
  const notifyError = (msg: string) => toast.show({ variant: 'error', description: msg });

  const [createOpen, setCreateOpen] = useState(false);
  const [createAreaCode, setCreateAreaCode] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [areaCreateOpen, setAreaCreateOpen] = useState(false);
  const [editArea, setEditArea] = useState<ModuleAreaRow | null>(null);
  const [pendingDeleteArea, setPendingDeleteArea] = useState<ModuleAreaRow | null>(null);
  const [pendingDeleteModule, setPendingDeleteModule] = useState<ModuleTemplateRow | null>(null);

  const areasQuery = useQuery<{ areas: ModuleAreaRow[] }>({
    queryKey: ['admin', 'module-areas'],
    queryFn: async () => (await api.get('/admin/module-areas')).data,
    retry: false,
  });

  const templatesQuery = useQuery<{ templates: ModuleTemplateRow[] }>({
    queryKey: ['admin', 'module-templates'],
    queryFn: async () => (await api.get('/admin/module-templates')).data,
    retry: false,
  });

  const invalidateAreas = () =>
    void queryClient.invalidateQueries({ queryKey: ['admin', 'module-areas'] });

  // ── Mutacje szablonów ──────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: async (body: {
      slug: string;
      name: string;
      areaCode: string;
      description?: string | null;
      icon?: string | null;
      color?: string | null;
    }) => (await api.post('/admin/module-templates', body)).data,
    onSuccess: (created: ModuleTemplateRow) => {
      notifySuccess(t('moduleTemplates.toast.created'));
      setCreateOpen(false);
      setCreateAreaCode(undefined);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'module-templates'] });
      invalidateAreas();
      navigate(`/module-templates/${created.slug}`);
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      notifyError(
        code === 'TEMPLATE_SLUG_EXISTS'
          ? t('moduleTemplates.toast.slugExists')
          : code === 'AREA_NOT_FOUND'
            ? t('moduleTemplates.toast.areaNotFound')
            : (extractErrorDetail(err) ?? t('moduleTemplates.toast.createError')),
      );
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (slug: string) => (await api.delete(`/admin/module-templates/${slug}`)).data,
    onSuccess: () => {
      notifySuccess(t('moduleTemplates.toast.deleted'));
      setPendingDeleteModule(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'module-templates'] });
      invalidateAreas();
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      if (code === 'MODULE_HAS_INSTALLATIONS') {
        const count =
          (err as AxiosError<{ installationCount?: number }>).response?.data?.installationCount ??
          0;
        notifyError(t('moduleTemplates.toast.deleteHasInstallations', { count }));
      } else {
        notifyError(t('moduleTemplates.toast.deleteError'));
      }
      setPendingDeleteModule(null);
    },
  });

  // ── Mutacje obszarów ────────────────────────────────────────────────────────

  const createAreaMutation = useMutation({
    mutationFn: async (body: {
      code: string;
      name: string;
      icon?: string | null;
      color?: string | null;
    }) => (await api.post('/admin/module-areas', body)).data,
    onSuccess: () => {
      notifySuccess(t('moduleAreas.toast.created'));
      setAreaCreateOpen(false);
      invalidateAreas();
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      notifyError(
        code === 'AREA_CODE_EXISTS'
          ? t('moduleAreas.toast.codeExists')
          : (extractErrorDetail(err) ?? t('moduleAreas.toast.createError')),
      );
    },
  });

  const updateAreaMutation = useMutation({
    mutationFn: async (args: {
      id: string;
      body: { name: string; icon: string | null; color: string | null };
    }) => (await api.patch(`/admin/module-areas/${args.id}`, args.body)).data,
    onSuccess: () => {
      notifySuccess(t('moduleAreas.toast.updated'));
      setEditArea(null);
      invalidateAreas();
      // Nazwy obszarów siedzą też w wierszach szablonów (areaName) — odśwież nagłówki sekcji.
      void queryClient.invalidateQueries({ queryKey: ['admin', 'module-templates'] });
    },
    onError: () => notifyError(t('moduleAreas.toast.updateError')),
  });

  const deleteAreaMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/admin/module-areas/${id}`)).data,
    onSuccess: () => {
      notifySuccess(t('moduleAreas.toast.deleted'));
      setPendingDeleteArea(null);
      invalidateAreas();
    },
    onError: (err) => {
      const code = extractErrorCode(err);
      notifyError(
        code === 'AREA_HAS_MODULES'
          ? t('moduleAreas.toast.hasModules')
          : t('moduleAreas.toast.deleteError'),
      );
      setPendingDeleteArea(null);
    },
  });

  // Obszary posortowane po sortOrder asc (nulls last), potem po name.
  const sortedAreas = useMemo(() => {
    const areas = areasQuery.data?.areas ?? [];
    return [...areas].sort((a, b) => {
      if (a.sortOrder === null && b.sortOrder === null) return a.name.localeCompare(b.name);
      if (a.sortOrder === null) return 1;
      if (b.sortOrder === null) return -1;
      return a.sortOrder - b.sortOrder;
    });
  }, [areasQuery.data]);

  // Szablony poindeksowane po areaCode — do przypisania do sekcji obszaru.
  const templatesByArea = useMemo(() => {
    const templates = templatesQuery.data?.templates ?? [];
    const needle = search.trim().toLowerCase();
    const filtered =
      needle === ''
        ? templates
        : templates.filter(
            (tpl) =>
              tpl.name.toLowerCase().includes(needle) ||
              tpl.slug.toLowerCase().includes(needle) ||
              (tpl.description ?? '').toLowerCase().includes(needle),
          );
    const map = new Map<string, ModuleTemplateRow[]>();
    for (const tpl of filtered) {
      const key = tpl.areaCode ?? '__none__';
      const existing = map.get(key) ?? [];
      existing.push(tpl);
      map.set(key, existing);
    }
    return map;
  }, [templatesQuery.data, search]);

  const areas = areasQuery.data?.areas ?? [];

  // Szablony bez obszaru (areaCode === null lub nie pasuje do żadnego obszaru).
  const knownAreaCodes = new Set(areas.map((a) => a.code));
  const unassigned = useMemo(() => {
    const templates = templatesQuery.data?.templates ?? [];
    const needle = search.trim().toLowerCase();
    return templates.filter(
      (tpl) =>
        (tpl.areaCode === null || !knownAreaCodes.has(tpl.areaCode)) &&
        (needle === '' ||
          tpl.name.toLowerCase().includes(needle) ||
          tpl.slug.toLowerCase().includes(needle) ||
          (tpl.description ?? '').toLowerCase().includes(needle)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templatesQuery.data, search, areas]);

  // Przy aktywnej szukajce ukrywamy sekcje bez pasujących modułów.
  const needle = search.trim().toLowerCase();
  const visibleAreas =
    needle === ''
      ? sortedAreas
      : sortedAreas.filter((area) => (templatesByArea.get(area.code) ?? []).length > 0);

  const hasAnyResults =
    visibleAreas.length > 0 || (needle !== '' && unassigned.length > 0) || needle === '';

  return (
    <Layout>
      <section className="px-6 py-8">
        {/* ── Nagłówek strony ──────────────────────────────────────────────── */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{t('moduleTemplates.title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('moduleTemplates.subtitle')}
            </p>
          </div>
          <Button onClick={() => setAreaCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t('moduleAreas.newArea')}
          </Button>
        </div>

        {/* ── Szukajka ─────────────────────────────────────────────────────── */}
        <div className="mb-6 max-w-sm">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('moduleTemplates.searchPlaceholder')}
            aria-label={t('moduleTemplates.searchPlaceholder')}
          />
        </div>

        {/* ── Stan błędu ────────────────────────────────────────────────────── */}
        {templatesQuery.isError && (
          <div className="rounded border border-red-300 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
            {t('moduleTemplates.loadError')}
          </div>
        )}

        {/* ── Ładowanie ────────────────────────────────────────────────────── */}
        {(areasQuery.isLoading || templatesQuery.isLoading) && !templatesQuery.isError && (
          <Skeleton className="h-40 w-full" />
        )}

        {/* ── Brak obszarów — zachęta do dodania ───────────────────────────── */}
        {!areasQuery.isLoading && !areasQuery.isError && areas.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {t('moduleTemplates.empty')}
          </div>
        )}

        {/* ── Brak wyników wyszukiwania ─────────────────────────────────────── */}
        {!templatesQuery.isLoading &&
          !templatesQuery.isError &&
          areas.length > 0 &&
          needle !== '' &&
          !hasAnyResults && (
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-500 dark:border-gray-700 dark:text-gray-400">
              {t('moduleTemplates.emptyForSearch')}
            </div>
          )}

        {/* ── Sekcje obszarów ───────────────────────────────────────────────── */}
        {!areasQuery.isLoading && !templatesQuery.isLoading && !templatesQuery.isError && (
          <div className="flex flex-col gap-8">
            {visibleAreas.map((area) => {
              const sectionTemplates = templatesByArea.get(area.code) ?? [];
              return (
                <section key={area.id} data-testid={`area-section-${area.code}`}>
                  {/* Nagłówek sekcji */}
                  <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2 dark:border-gray-700">
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded"
                      style={{ backgroundColor: area.color ?? '#e5e7eb' }}
                      aria-hidden="true"
                    >
                      {renderIconByName(
                        area.icon ?? 'Briefcase',
                        `h-3.5 w-3.5 ${area.color ? 'text-white' : 'text-gray-500'}`,
                      )}
                    </span>
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                      {area.name}
                    </h2>
                    <span className="text-xs text-gray-400">{sectionTemplates.length}</span>
                    <button
                      type="button"
                      onClick={() => setEditArea(area)}
                      aria-label={t('moduleAreas.editAreaAria', { name: area.name })}
                      className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDeleteArea(area)}
                      disabled={area.templateCount > 0}
                      aria-label={t('moduleAreas.deleteAreaAria', { name: area.name })}
                      title={area.templateCount > 0 ? t('moduleAreas.hasModulesHint') : undefined}
                      className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-gray-800 dark:hover:text-red-400"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      data-testid={`add-module-${area.code}`}
                      onClick={() => {
                        setCreateAreaCode(area.code);
                        setCreateOpen(true);
                      }}
                      aria-label={t('moduleTemplates.addModule')}
                      title={t('moduleTemplates.addModule')}
                      className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-accent-600 dark:hover:bg-gray-800 dark:hover:text-accent-400"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Kafelki modułów — pusta sekcja zostaje samym paskiem (bez hintu) */}
                  {sectionTemplates.length > 0 && (
                    <div className="flex flex-wrap gap-4">
                      {sectionTemplates.map((tpl) => (
                        <ModuleTile
                          key={tpl.id}
                          tpl={tpl}
                          accentColor={area.color}
                          onOpen={(slug) => navigate(`/module-templates/${slug}`)}
                          onDelete={() => setPendingDeleteModule(tpl)}
                        />
                      ))}
                    </div>
                  )}
                </section>
              );
            })}

            {/* Sekcja „Bez obszaru" — szablony bez przypisanego obszaru */}
            {unassigned.length > 0 && (
              <section data-testid="area-section-__none__">
                <div className="mb-3 flex items-center gap-2 border-b border-gray-200 pb-2 dark:border-gray-700">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                    {t('moduleTemplates.noArea')}
                  </h2>
                  <span className="text-xs text-gray-400">{unassigned.length}</span>
                </div>
                <div className="flex flex-wrap gap-4">
                  {unassigned.map((tpl) => (
                    <ModuleTile
                      key={tpl.id}
                      tpl={tpl}
                      onOpen={(slug) => navigate(`/module-templates/${slug}`)}
                      onDelete={() => setPendingDeleteModule(tpl)}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        <CreateTemplateModal
          open={createOpen}
          onClose={() => {
            setCreateOpen(false);
            setCreateAreaCode(undefined);
          }}
          areas={areas}
          initialAreaCode={createAreaCode}
          pending={createMutation.isPending}
          onSubmit={(body) => createMutation.mutate(body)}
        />

        <AreaModal
          mode="create"
          open={areaCreateOpen}
          onClose={() => setAreaCreateOpen(false)}
          pending={createAreaMutation.isPending}
          onSubmit={(values) =>
            createAreaMutation.mutate({
              code: values.code,
              name: values.name,
              icon: values.icon || null,
              color: values.color || null,
            })
          }
        />

        {editArea && (
          <AreaModal
            mode="edit"
            open={editArea !== null}
            area={editArea}
            onClose={() => setEditArea(null)}
            pending={updateAreaMutation.isPending}
            onSubmit={(values) =>
              updateAreaMutation.mutate({
                id: editArea.id,
                body: { name: values.name, icon: values.icon || null, color: values.color || null },
              })
            }
          />
        )}

        <ConfirmDialog
          open={pendingDeleteArea !== null}
          variant="danger"
          title={t('moduleAreas.deleteTitle')}
          message={
            pendingDeleteArea
              ? t('moduleAreas.deleteMessage', { name: pendingDeleteArea.name })
              : ''
          }
          confirmLabel={t('common.delete')}
          onConfirm={() => {
            if (pendingDeleteArea) deleteAreaMutation.mutate(pendingDeleteArea.id);
          }}
          onClose={() => setPendingDeleteArea(null)}
        />

        <ConfirmDialog
          open={pendingDeleteModule !== null}
          variant="danger"
          title={t('moduleTemplates.deleteTemplateTitle')}
          message={
            pendingDeleteModule
              ? t('moduleTemplates.deleteTemplateMessage', { name: pendingDeleteModule.name })
              : ''
          }
          confirmLabel={t('common.delete')}
          onConfirm={() => {
            if (pendingDeleteModule) deleteTemplateMutation.mutate(pendingDeleteModule.slug);
          }}
          onClose={() => setPendingDeleteModule(null)}
        />
      </section>
    </Layout>
  );
}

// ---------------------------------------------------------------------------
// Modal create szablonu
// ---------------------------------------------------------------------------

function CreateTemplateModal({
  open,
  onClose,
  areas,
  initialAreaCode,
  onSubmit,
  pending,
}: {
  open: boolean;
  onClose: () => void;
  areas: ModuleAreaRow[];
  /** Obszar preselekcji w modalu (brak = pierwszy z listy). */
  initialAreaCode?: string;
  onSubmit: (body: {
    slug: string;
    name: string;
    areaCode: string;
    description?: string | null;
    icon?: string | null;
    color?: string | null;
  }) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open) {
      setSlug('');
      setName('');
      setAreaCode(initialAreaCode ?? areas[0]?.code ?? '');
      setDescription('');
    }
  }, [open, areas, initialAreaCode]);

  const slugValid = Boolean(slug) && MODULE_TEMPLATE_SLUG_PATTERN.test(slug);
  const canSubmit = !pending && slugValid && Boolean(name) && Boolean(areaCode);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('moduleTemplates.newTemplate')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                slug,
                name,
                areaCode,
                description: description || null,
              })
            }
            disabled={!canSubmit}
          >
            {t('common.create')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField label={t('moduleTemplates.field.area')}>
          <Dropdown
            options={areas.map((a) => ({ value: a.code, label: a.name }))}
            value={areaCode}
            onChange={(v) => setAreaCode(v)}
          />
        </FormField>
        <Input
          label={t('moduleTemplates.field.slug')}
          hint={t('moduleTemplates.field.slugHint')}
          error={slug !== '' && !slugValid ? t('moduleTemplates.field.slugInvalid') : undefined}
          value={slug}
          onChange={(e) => setSlug(normalizeSlugInput(e.target.value))}
          placeholder="np. urlopy"
        />
        <Input
          label={t('moduleTemplates.field.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('moduleTemplates.field.namePlaceholder')}
        />
        <Input
          label={t('moduleTemplates.field.description')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modal create/edit obszaru
// ---------------------------------------------------------------------------

function AreaModal({
  mode,
  open,
  area,
  onClose,
  onSubmit,
  pending,
}: {
  mode: 'create' | 'edit';
  open: boolean;
  area?: ModuleAreaRow;
  onClose: () => void;
  onSubmit: (values: AreaFormValues) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('');
  const [color, setColor] = useState('');

  useEffect(() => {
    if (open) {
      setCode(area?.code ?? '');
      setName(area?.name ?? '');
      setIcon(area?.icon ?? '');
      setColor(area?.color ?? '');
    }
  }, [open, area]);

  const colorValid = color === '' || /^#[0-9a-fA-F]{6}$/.test(color);
  // Kod walidowany tylko przy tworzeniu (w edycji jest niezmienny i disabled).
  const codeValid = mode === 'edit' || (Boolean(code) && MODULE_TEMPLATE_SLUG_PATTERN.test(code));
  const canSubmit = !pending && Boolean(name) && codeValid && colorValid;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? t('moduleAreas.newArea') : t('moduleAreas.editArea')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => onSubmit({ code, name, icon, color })} disabled={!canSubmit}>
            {mode === 'create' ? t('common.create') : t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label={t('moduleAreas.field.code')}
          hint={mode === 'create' ? t('moduleAreas.field.codeHint') : undefined}
          error={
            mode === 'create' && code !== '' && !codeValid
              ? t('moduleAreas.field.codeInvalid')
              : undefined
          }
          value={code}
          disabled={mode === 'edit'}
          onChange={(e) => setCode(normalizeSlugInput(e.target.value))}
          placeholder="np. hr"
        />
        <Input
          label={t('moduleAreas.field.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('moduleAreas.field.namePlaceholder')}
        />
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-text-secondary">
            {t('moduleAreas.field.icon')}
          </label>
          <IconPicker
            value={icon || 'Briefcase'}
            onChange={setIcon}
            accentColor={colorValid && color ? color : undefined}
            ariaLabel={t('moduleAreas.field.icon')}
          />
          <span className="text-xs text-text-muted">{t('moduleAreas.field.iconHint')}</span>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-text-secondary">
            {t('moduleAreas.field.color')}
          </label>
          <ColorPicker
            value={color || undefined}
            onChange={(hex) => setColor(hex ?? '')}
            label={t('moduleAreas.field.color')}
          />
          <span className="text-xs text-text-muted">{t('moduleAreas.field.colorHint')}</span>
        </div>
      </div>
    </Modal>
  );
}
